/**
 * Move History Page (ASS-76, renamed by AST-119)
 *
 * Mark's manual-entry log. Columns: Date/time, Truck ID, Project/Yard,
 * From, To, Asset, Quantity (signed, coloured), User. Newest first.
 *
 * AST-119: was "Entry Log" with a Mat Type column + filter. Now "Move
 * History", and the column + filter are "Asset": manual asset names
 * (ST124), with moves from before manual assets falling back to their
 * subtype name (Timber Mat). Naming/filter rules: utils/moveHistory.
 * This page only ever lists manual and Both projects, so beacon projects
 * are unaffected (MH-9).
 *
 * "he'd like to see it so he can better determine if we don't need it" --
 * operator ruling, 2026-09-10: View entries is on the demo path even though
 * Mark may decide afterward it's not needed.
 *
 * Reads entries from state.count.entriesByProject[projectId] only -- never
 * /counts or scopes. The fetchScopes call that lived here before has been
 * removed entirely: it kept calling /scopes, logging [FIXTURE] and tripping
 * ASS-64's falsifier for a page that no longer shows scope data.
 *
 * Route: /manual/log?projectId=<id>
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import { ChevronLeft as BackIcon } from '@mui/icons-material';

import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import DataTable from '../elements/DataTable';
import AppBar from '../elements/AppBar';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { fetchEntries } from '../../store/slices/countSlice';
import { fetchAssetTypes, fetchAssetSubTypes } from '../../store/slices/assetSlice';
import { fetchAreaElements } from '../../services/areaElementsApi';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { assetLabel, assetFilterOptions, filterByAsset } from '../../utils/moveHistory';

const EntryLogPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeCustomer = useAppSelector((state) => state.customer.activeCustomer);
  const projectListRaw = useAppSelector((state) => state.project.projectList);
  const projectList = useMemo(() => projectListRaw || [], [projectListRaw]);
  const { assetTypes, assetSubTypes } = useAppSelector((state) => state.asset);
  const [searchParams] = useSearchParams();

  // Preset the Project filter from the ?projectId= param -- the door added
  // in ProjectPage (View entries, beside Enter Asset Move) always supplies
  // this. Previously this page only read ?scope=/?location=, which no
  // longer exist as filters.
  const presetProjectId = searchParams.get('projectId') || '';

  const projects = useMemo(
    () => projectList.filter((p) => p.trackingMode === 'manual' || p.trackingMode === 'hybrid'),
    [projectList]
  );

  const [selectedProjectId, setSelectedProjectId] = useState(presetProjectId);
  const [filterAssetId, setFilterAssetId] = useState('');

  // If the param arrives after the initial render (e.g. project list loads
  // async), pick it up once rather than only on mount.
  useEffect(() => {
    if (presetProjectId && presetProjectId !== selectedProjectId) {
      setSelectedProjectId(presetProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetProjectId]);

  useEffect(() => {
    if (!activeCustomer?.id) return;
    dispatch(fetchProjectsByCustomer(activeCustomer.id));
  }, [dispatch, activeCustomer?.id]);

  // Default to the first eligible project if nothing is selected/preset yet.
  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    if (!assetTypes || assetTypes.length === 0) dispatch(fetchAssetTypes());
    if (!assetSubTypes || assetSubTypes.length === 0) dispatch(fetchAssetSubTypes());
  }, [assetTypes, assetSubTypes, dispatch]);

  const entriesForProject = useAppSelector(
    (state) => (selectedProjectId ? state.count.entriesByProject[selectedProjectId] : null)
  );

  const loading = useAppSelector((state) => state.count.loading);
  const error = useAppSelector((state) => state.count.entriesError);

  useEffect(() => {
    if (!selectedProjectId) return;
    dispatch(fetchEntries({ projectId: selectedProjectId, filters: {} }));
  }, [selectedProjectId, dispatch]);

  // AST-90: names for the From / To columns. includeRetired so an entry
  // made before a location was removed still shows its name.
  const [elementNameById, setElementNameById] = useState({});
  useEffect(() => {
    setElementNameById({});
    if (!selectedProjectId) return undefined;
    let cancelled = false;
    fetchAreaElements(selectedProjectId, { includeRetired: true })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const e of res?.items || []) map[e.id] = e.retired ? `${e.name} (removed)` : e.name;
        setElementNameById(map);
      })
      .catch(() => {}); // columns fall back to blank; the log itself still loads
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // AST-119: the project customer's manual assets, ARCHIVED INCLUDED -- a
  // past move against ST84 must still be nameable and findable (MH-5, AC-6).
  // Same project-scoped read Enter Asset Move uses, so ordinary users of the
  // customer can see names without admin rights. Non-fatal: on failure the
  // Asset column falls back to subtype names and the filter is empty.
  const [manualAssets, setManualAssets] = useState([]);
  useEffect(() => {
    setManualAssets([]);
    setFilterAssetId(''); // a filter from another project's assets is meaningless here
    if (!selectedProjectId) return undefined;
    let cancelled = false;
    fetchProjectManualAssets(selectedProjectId)
      .then((res) => { if (!cancelled) setManualAssets(res?.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // Subtype names under the "Mat" parent type, matching Enter Asset Move's
  // resolution -- read live from the catalog, never hard-coded. Used only as
  // the fallback label for moves with no manual asset (MH-4).
  const matType = useMemo(() => (assetTypes || []).find((t) => t.name === 'Mat') || null, [assetTypes]);
  const matTypeNameById = useMemo(() => {
    const map = {};
    for (const s of (assetSubTypes || [])) if (matType && s.typeId === matType.id) map[s.id] = s.name;
    return map;
  }, [assetSubTypes, matType]);

  const assetById = useMemo(() => Object.fromEntries(manualAssets.map((a) => [a.id, a])), [manualAssets]);
  const assetOptions = useMemo(() => assetFilterOptions(manualAssets, matType?.id), [manualAssets, matType]);
  const selectedAssetOption = assetOptions.find((o) => o.id === filterAssetId) || null;

  const projectNameById = useMemo(() => {
    const map = {};
    for (const p of projectList) map[p.id] = p.name;
    return map;
  }, [projectList]);

  const filteredEntries = useMemo(() => {
    const rows = filterByAsset(entriesForProject || [], filterAssetId); // MH-6
    // Newest first.
    return [...rows].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [entriesForProject, filterAssetId]);

  const isFiltered = !!filterAssetId;

  const columns = [
    {
      key: 'timestamp',
      label: 'Date/time',
      sortable: true,
      render: (val) => {
        if (!val) return '—';
        const d = new Date(val);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      },
    },
    {
      key: 'truckId',
      label: 'Truck ID',
      render: (val) => val || '—',
    },
    {
      key: 'projectId',
      label: 'Project/Yard',
      render: (val) => projectNameById[val] || val || '—',
    },
    // AST-90: From / To. Blank when not set. No Distance column -- Mark,
    // 2026-10-07: "we will not calculate distance for the manually tracked
    // mats yet".
    {
      key: 'fromElementId',
      label: 'From',
      render: (val) => (val ? elementNameById[val] || '—' : ''),
    },
    {
      key: 'toElementId',
      label: 'To',
      render: (val) => (val ? elementNameById[val] || '—' : ''),
    },
    // AST-119 MH-3/4/7: manual asset name, live by id; subtype for older moves.
    {
      key: 'manualAssetId',
      label: 'Asset',
      render: (_val, row) => assetLabel(row, assetById, matTypeNameById),
    },
    {
      key: 'quantity',
      label: 'Quantity',
      align: 'right',
      sortable: true,
      render: (val) => {
        const n = val || 0;
        const prefix = n > 0 ? '+' : '';
        return (
          <Box component="span" sx={{ color: n < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
            {prefix}{n}
          </Box>
        );
      },
    },
    {
      key: 'userEmail',
      label: 'User',
      render: (val) => val || '—',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ASS-76 follow-up: this page had no AppBar at all -- /manual/log was
          a dead end with no way to open the nav or return Home. Same
          pattern as ProjectListPage.jsx: no title/projectSelector props
          needed, AppBar falls back to its own "Projects"/project-name
          display. */}
      <AppBar />
      <PageBody>
        {/* ASS-76 P3: a way back to the project the log was opened from.
            Uses presetProjectId (the ?projectId= URL param, i.e. what
            ProjectPage's "View entries" door actually supplied) rather
            than selectedProjectId, which the Project filter dropdown
            below can change independently -- the back button should
            always return to where the user came from, not wherever the
            filter happens to be pointed. Same styling as the Projects
            list's "Home" button. */}
        {presetProjectId && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Button
              variant="text"
              color="secondary"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/projects/${presetProjectId}`)}
              sx={{ height: 44, mb: 2 }}
            >
              {projectNameById[presetProjectId] || 'Project'}
            </Button>
          </Box>
        )}
        <Box sx={{ px: 3, pt: presetProjectId ? 0 : 2, pb: 1 }}>
          <PageTitle>Move History</PageTitle>
        </Box>

        {/* Filters: Project and Asset only. */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  label="Project"
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                >
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* AST-119 MH-5: one flat A->Z list of the customer's manual
                assets, archived included, with type-to-filter. */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={assetOptions}
                value={selectedAssetOption}
                onChange={(_e, opt) => setFilterAssetId(opt?.id || '')}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText="No manual assets"
                renderInput={(params) => <TextField {...params} label="Asset" placeholder="All" />}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ px: 3, pb: 3 }}>
          <DataTable
            columns={columns}
            rows={filteredEntries}
            loading={loading}
            error={error}
            emptyMessage="No moves yet — use Enter Asset Move on a project."
            noMatchMessage="No moves match your filters"
            isFiltered={isFiltered}
            onRetry={() => setSelectedProjectId((prev) => prev)}
          />
        </Box>
      </PageBody>
    </Box>
  );
};

export default EntryLogPage;
